'use client';

import { Suspense, useRef, useEffect, ReactNode } from 'react';
import { useVisualEditing } from '@/hooks/useVisualEditing';
import { useRouter, usePathname } from 'next/navigation';
import NavigationBar from '@/components/layout/NavigationBar';
import Footer from '@/components/layout/Footer';

interface VisualEditingLayoutProps {
	headerNavigation: any;
	footerNavigation: any;
	globals: any;
	children: ReactNode;
}

/**
 * Inner component that uses hooks requiring Suspense (useSearchParams via useVisualEditing).
 */
function VisualEditingLayoutInner({
	headerNavigation,
	footerNavigation,
	globals,
	children,
}: VisualEditingLayoutProps) {
	const navRef = useRef<HTMLElement>(null);
	const footerRef = useRef<HTMLElement>(null);
	const { isVisualEditingEnabled, apply } = useVisualEditing();
	const router = useRouter();

	useEffect(() => {
		if (isVisualEditingEnabled) {
			// Apply visual editing for the navigation bar if its ref is set.
			if (navRef.current) {
				apply({
					elements: [navRef.current],
					onSaved: () => window.location.reload(),
				});
			}
			// Apply visual editing for the footer if its ref is set.
			if (footerRef.current) {
				apply({
					elements: [footerRef.current],
					onSaved: () => window.location.reload(),
				});
			}
		}
	}, [isVisualEditingEnabled, apply, router]);

	const pathname = usePathname();
	const isHomePage = pathname === '/';

	return (
		<>
			<NavigationBar ref={navRef} navigation={headerNavigation} globals={globals} />
			{children}
			<Footer ref={footerRef} navigation={footerNavigation} globals={globals} />
		</>
	);
}

/**
 * Layout wrapper with Suspense boundary to support useSearchParams
 * in useVisualEditing hook without blocking static page prerendering.
 */
export default function VisualEditingLayout(props: VisualEditingLayoutProps) {
	return (
		<Suspense
			fallback={
				<>
					<NavigationBar navigation={props.headerNavigation} globals={props.globals} />
					{props.children}
					<Footer navigation={props.footerNavigation} globals={props.globals} />
				</>
			}
		>
			<VisualEditingLayoutInner {...props} />
		</Suspense>
	);
}
